import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api } from "../lib/api";
import { Clock, Ruler, DollarSign, Zap } from "lucide-react";
import DateFilter from "../components/layout/DateFilter";
import KpiCard from "../components/KpiCard";
import HoursHeatmap from "../components/HoursHeatmap";
import MaterialTreemap from "../components/MaterialTreemap";
import KpiDrillModal from "../components/KpiDrillModal";
import { useAuth } from "../context/AuthContext";
import { canViewAny, firstAllowedRoute } from "../lib/permissions";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  PieChart, Pie, Cell, Legend, LineChart, Line,
} from "recharts";

const DEPT_COLORS = {
  MOLD: "#EC4899",
  FRAME: "#94A3B8",
  GFRC: "#10B981",
  FINISH: "#A855F7",
  FIBERGLASS: "#F59E0B",
};
const DEPARTMENTS = ["MOLD", "FRAME", "GFRC", "FINISH", "FIBERGLASS"];

function fmtNum(n) {
  if (n === null || n === undefined || isNaN(n)) return "—";
  if (Math.abs(n) >= 1_000_000) return (n / 1_000_000).toFixed(2) + "M";
  if (Math.abs(n) >= 1000) return (n / 1000).toFixed(2) + "K";
  return Number(n).toLocaleString();
}

export default function Overview() {
  const { user } = useAuth();
  // If this user has no Overview permission, send them straight to the first
  // module they CAN view. Fixes the "lands on Overview, must refresh to reach
  // assigned department" bug for restricted users (e.g. MOLD-only operator).
  const canSeeOverview = canViewAny(user, "overview");
  const isAdmin = (user?.role_name === "Administrator") || (user?.role === "admin");
  const plantScope = String(user?.plant_scope || "BOTH").toUpperCase();
  const canSeeSLC = isAdmin || plantScope === "SLC" || plantScope === "BOTH";
  const canSeeMex = isAdmin || plantScope === "MEX" || plantScope === "BOTH";
  const userDeptsList = (user?.departments || []).map((d) => String(d).toUpperCase());
  const visibleDepts = (isAdmin || userDeptsList.length === 0)
    ? DEPARTMENTS
    : DEPARTMENTS.filter((d) => userDeptsList.includes(d));
  const [filter, setFilter] = useState({ preset: "all" });
  // Auto-toggle Mexico view for users whose plant_scope locks them there.
  const [showMexicoState, setShowMexico] = useState(() => !canSeeSLC && canSeeMex);
  const showMexico = (!canSeeMex) ? false : ((!canSeeSLC) ? true : showMexicoState);
  const [kpis, setKpis] = useState(null);
  const [prod, setProd] = useState([]);
  const [hoursTrend, setHoursTrend] = useState([]);
  const [hoursByDept, setHoursByDept] = useState([]);
  const [otTrend, setOtTrend] = useState([]);
  const [perf, setPerf] = useState([]);
  const [matCat, setMatCat] = useState([]);
  const [topJobs, setTopJobs] = useState([]);
  const [matWeek, setMatWeek] = useState([]);
  const [matAll, setMatAll] = useState([]);
  const [heatmap, setHeatmap] = useState(null);
  const [drill, setDrill] = useState(null);   // 'hours' | 'production' | 'materials' | null
  const [deptDrill, setDeptDrill] = useState(null);  // {department, unit} | null

  useEffect(() => {
    if (!canSeeOverview) return;
    const params = { preset: filter.preset, show_mexico: showMexico };
    if (filter.preset === "custom") { params.start = filter.start; params.end = filter.end; }
    (async () => {
      const [k, p] = await Promise.all([
        api.get("/overview/kpis", { params }),
        api.get("/overview/production-by-dept", { params }),
      ]);
      setKpis(k.data); setProd(p.data);
    })();
  }, [filter, showMexico, canSeeOverview]);

  useEffect(() => {
    if (!canSeeOverview) return;
    (async () => {
      const [a, b, c, d, e, f, g, h, t] = await Promise.all([
        api.get("/overview/weekly-hours-trend", { params: { show_mexico: showMexico } }),
        api.get("/overview/hours-by-dept",      { params: { show_mexico: showMexico } }),
        api.get("/overview/weekly-ot-trend",    { params: { show_mexico: showMexico } }),
        api.get("/overview/dept-performance", { params: { show_mexico: showMexico } }),
        api.get("/overview/material-by-category", { params: { show_mexico: showMexico } }),
        api.get("/overview/top-jobs-material",    { params: { show_mexico: showMexico } }),
        api.get("/overview/material-by-week",     { params: { show_mexico: showMexico } }),
        api.get("/overview/hours-heatmap",        { params: { show_mexico: showMexico } }),
        api.get("/overview/material-by-category", { params: { limit: 0, show_mexico: showMexico } }),
      ]);
      setHoursTrend(a.data); setHoursByDept(b.data); setOtTrend(c.data);
      setPerf(d.data); setMatCat(e.data); setTopJobs(f.data); setMatWeek(g.data);
      setHeatmap(h.data); setMatAll(t.data);
    })();
  }, [showMexico, canSeeOverview]);

  // Restricted user landed on "/" — bounce them to their first allowed module.
  if (user && !canSeeOverview) {
    return <Navigate to={firstAllowedRoute(user)} replace />;
  }

  return (
    <div className="space-y-6" data-testid="overview-page">
      <header>
        <div className="text-[11px] tracking-[0.35em] uppercase text-[#00D4FF]">01 · Overview</div>
        <h1 className="font-heading font-bold text-4xl sm:text-5xl tracking-tight mt-2">Production Overview</h1>
        <p className="text-sm text-muted-foreground mt-1">Executive summary of production metrics across all departments</p>
      </header>

      <div className="flex items-center justify-between gap-4 flex-wrap">
        <DateFilter value={filter} onChange={setFilter} />
        {canSeeSLC && canSeeMex ? (
          <label className="flex items-center gap-3 cursor-pointer select-none" data-testid="show-mexico-toggle">
            <span className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground font-semibold">Show Mexico Plants</span>
            <button
              type="button"
              role="switch"
              aria-checked={showMexico}
              onClick={() => setShowMexico((v) => !v)}
              className={`relative h-6 w-11 transition-colors ${showMexico ? "bg-[#00D4FF]" : "bg-[#1A1C28] border border-border"}`}
            >
              <span
                className={`absolute top-0.5 h-5 w-5 transition-transform ${
                  showMexico ? "translate-x-[22px] bg-[#0A0B10]" : "translate-x-0.5 bg-[#94A3B8]"
                }`}
              />
            </button>
          </label>
        ) : (
          <span
            className="text-[10px] tracking-widest uppercase font-mono px-2 py-1 border border-border bg-[#0A0B10] text-muted-foreground"
            data-testid="plant-scope-locked"
            title={`Your account is scoped to ${canSeeMex ? "Mexico" : "SLC"} plants only`}
          >
            Plant: {canSeeMex ? "Mexico" : "SLC"} only
          </span>
        )}
      </div>

      {/* KPI ROW — per user preference, cards 1-3 display the RATE / PERCENT
           as the primary hero value with the raw total demoted to the
           accent chip. SF/Hour card (4th) keeps its original layout. */}
      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard testId="kpi-total-hours" label="Total Hours"
          value={showMexico ? "0" : (kpis ? `${kpis.overtime_pct}%` : "—")} unit="OT" icon={Clock} accent
          sub={showMexico ? "—" : (kpis ? `${fmtNum(kpis.total_hours)} HRS` : null)}
          onClick={showMexico ? undefined : () => setDrill("hours")}
        />
        <KpiCard testId="kpi-production-sf" label="Production SF"
          value={kpis?.sf_per_hour ?? "—"} unit="SF/HR" icon={Ruler}
          sub={kpis ? `${fmtNum(kpis.production_sf)} SF` : null}
          onClick={() => setDrill("production")}
        />
        <KpiCard testId="kpi-material-cost" label="Material Cost"
          value={showMexico ? "$0" : (kpis ? `$${kpis.cost_per_sf}` : "—")} unit="/SF" icon={DollarSign}
          sub={showMexico ? "—" : (kpis ? `$${fmtNum(kpis.material_cost)} TOTAL` : null)}
          onClick={showMexico ? undefined : () => setDrill("materials")}
        />
        <KpiCard testId="kpi-sf-per-hour" label="SF / Hour"
          value={kpis?.sf_per_hour ?? "—"} unit="sf/h" icon={Zap}
          sub={kpis ? `${fmtNum(kpis.pieces_count)} pieces` : null}
        />
      </section>

      {/* PRODUCTION BY DEPT CARDS */}
      <section className="bg-[#12141D] border border-border p-5">
        <h2 className="font-heading font-semibold">Production by Department</h2>
        <p className="text-xs text-muted-foreground mb-4">Mold, GFRC, Finish, Fiberglass in SQFT — Frame in piece count — per Job</p>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {prod.filter((d) => visibleDepts.includes(String(d.department).toUpperCase())).map((d) => (
            <button key={d.department} data-testid={`prod-card-${d.department.toLowerCase()}`}
                 type="button"
                 onClick={() => setDeptDrill({ department: d.department, unit: d.metric_unit })}
                 className="bg-[#0A0B10] border border-border p-4 text-left hover:border-[#00D4FF] transition-colors group relative">
              <div className="text-[10px] tracking-[0.3em] uppercase text-center text-muted-foreground">{d.department}</div>
              <div className="font-heading font-bold text-3xl text-center mt-3 tabular-nums">
                {fmtNum(d.metric_value)}
              </div>
              <div className="text-center text-[11px] text-muted-foreground mt-1">
                {d.metric_unit} · {d.jobs} jobs
              </div>
              <div className="mt-3 flex justify-center">
                <span className="px-2 py-0.5 border border-emerald-500/30 bg-emerald-500/10 text-emerald-300 text-[9px] tracking-widest uppercase">
                  Completed
                </span>
              </div>
              <span className="absolute top-2 right-2 text-[9px] tracking-widest text-[#00D4FF] opacity-0 group-hover:opacity-100 transition-opacity">
                DRILL →
              </span>
            </button>
          ))}
        </div>
      </section>

      {/* CHARTS ROW: Weekly Hours + Hours by Dept */}
      <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-[#12141D] border border-border p-5 lg:col-span-2">
          <h3 className="font-heading font-semibold">Weekly Hours Trend</h3>
          <p className="text-xs text-muted-foreground mb-3">Regular + Overtime stacked</p>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
              <BarChart data={hoursTrend}>
                <CartesianGrid stroke="#1E293B" vertical={false} />
                <XAxis dataKey="week" stroke="#94A3B8" tick={{ fontSize: 10, fill: "#94A3B8" }}
                       tickFormatter={(v) => v?.slice(5)} />
                <YAxis stroke="#94A3B8" tick={{ fontSize: 10, fill: "#94A3B8" }} />
                <Tooltip contentStyle={{ background: "#0A0B10", border: "1px solid #1E293B", borderRadius: 0 }} />
                <Bar dataKey="regular"  stackId="h" fill="#00D4FF" />
                <Bar dataKey="overtime" stackId="h" fill="#10B981" />
                <Legend wrapperStyle={{ fontSize: 11, color: "#94A3B8" }} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-[#12141D] border border-border p-5">
          <h3 className="font-heading font-semibold">Hours by Department</h3>
          <p className="text-xs text-muted-foreground mb-3">Distribution</p>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
              <PieChart>
                <Pie data={hoursByDept.filter((d) => visibleDepts.includes(String(d.department).toUpperCase()))} dataKey="hours" nameKey="department" outerRadius={90} innerRadius={55} paddingAngle={2}>
                  {hoursByDept.filter((d) => visibleDepts.includes(String(d.department).toUpperCase())).map((d) => (
                    <Cell key={d.department} fill={DEPT_COLORS[d.department] || "#00D4FF"} stroke="#0A0B10" />
                  ))}
                </Pie>
                <Legend wrapperStyle={{ fontSize: 10, color: "#94A3B8" }} />
                <Tooltip
                  cursor={false}
                  wrapperStyle={{ outline: "none", zIndex: 50 }}
                  contentStyle={{ background: "rgba(10,11,16,0.95)", border: "1px solid #00D4FF", borderRadius: 0, padding: "6px 10px" }}
                  itemStyle={{ color: "#F8FAFC", fontSize: 11 }}
                  labelStyle={{ color: "#00D4FF", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.1em" }}
                  formatter={(value, name) => [`${Number(value).toLocaleString()} hrs`, name]}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>

      {/* WEEKLY OT TREND */}
      <section className="bg-[#12141D] border border-border p-5">
        <h3 className="font-heading font-semibold">Weekly Overtime Trend</h3>
        <p className="text-xs text-muted-foreground mb-3">OT Hours by Department</p>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
            <LineChart data={otTrend}>
              <CartesianGrid stroke="#1E293B" strokeDasharray="3 3" />
              <XAxis dataKey="week" stroke="#94A3B8" tick={{ fontSize: 10, fill: "#94A3B8" }}
                     tickFormatter={(v) => v?.slice(5)} />
              <YAxis stroke="#94A3B8" tick={{ fontSize: 10, fill: "#94A3B8" }} />
              <Tooltip contentStyle={{ background: "#0A0B10", border: "1px solid #1E293B", borderRadius: 0 }} />
              <Legend wrapperStyle={{ fontSize: 11, color: "#94A3B8" }} />
              {visibleDepts.map((d) => (
                <Line key={d} type="monotone" dataKey={d} stroke={DEPT_COLORS[d]} strokeWidth={2} dot={{ r: 2 }} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </section>

      {/* DEPT PERFORMANCE TABLE */}
      <section className="bg-[#12141D] border border-border">
        <div className="p-5 pb-3">
          <h3 className="font-heading font-semibold">Department Performance</h3>
          <p className="text-xs text-muted-foreground">All departments — production, efficiency, cost, OT · click a row to drill</p>
        </div>
        <table className="w-full text-left">
          <thead className="text-[10px] uppercase tracking-widest text-[#94A3B8] bg-[#1A1C28]">
            <tr>
              <th className="py-3 px-5">Department</th>
              <th className="py-3 px-5">Hours</th>
              <th className="py-3 px-5">Production</th>
              <th className="py-3 px-5">Efficiency</th>
              <th className="py-3 px-5">Material Cost</th>
              <th className="py-3 px-5 text-right">Overtime %</th>
            </tr>
          </thead>
          <tbody>
            {perf.filter((d) => visibleDepts.includes(String(d.department).toUpperCase())).map((d) => (
              <tr key={d.department}
                  data-testid={`perf-row-${d.department.toLowerCase()}`}
                  onClick={() => setDeptDrill({ department: d.department, unit: d.production_unit })}
                  className="border-t border-border cursor-pointer hover:bg-[#1A1C28]/60 transition-colors">
                <td className="py-3 px-5 font-heading font-semibold">{d.department}</td>
                <td className="py-3 px-5 font-mono text-sm">{d.hours === null ? <span className="text-muted-foreground">—</span> : fmtNum(d.hours)}</td>
                <td className="py-3 px-5 font-mono text-sm">
                  <span className="text-emerald-300">{fmtNum(d.production_value)}</span>
                  <span className="text-muted-foreground"> {d.production_unit}</span>
                </td>
                <td className="py-3 px-5 font-mono text-sm">
                  {d.efficiency === null ? <span className="text-muted-foreground">—</span> :
                   <>{d.efficiency} <span className="text-muted-foreground">{d.efficiency_unit}</span></>}
                </td>
                <td className="py-3 px-5 font-mono text-sm">
                  {d.material_cost === null ? <span className="text-muted-foreground">—</span> : `$${fmtNum(d.material_cost)}`}
                </td>
                <td className="py-3 px-5 font-mono text-sm text-right">
                  {d.overtime_pct === null ? <span className="text-muted-foreground">—</span> :
                   <span className={d.overtime_pct >= 15 ? "text-amber-300" : "text-emerald-300"}>{d.overtime_pct}%</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {/* MATERIAL CHARTS ROW */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-[#12141D] border border-border p-5">
          <h3 className="font-heading font-semibold mb-3">Material Cost by Category</h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
              <BarChart data={matCat} layout="vertical" margin={{ left: 30 }}>
                <CartesianGrid stroke="#1E293B" horizontal={false} />
                <XAxis type="number" stroke="#94A3B8" tick={{ fontSize: 10, fill: "#94A3B8" }} />
                <YAxis dataKey="category" type="category" stroke="#94A3B8" tick={{ fontSize: 10, fill: "#94A3B8" }} width={120} />
                <Tooltip contentStyle={{ background: "#0A0B10", border: "1px solid #1E293B", borderRadius: 0 }}
                         formatter={(v) => `$${Number(v).toLocaleString()}`} />
                <Bar dataKey="cost" fill="#F59E0B" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-[#12141D] border border-border p-5">
          <h3 className="font-heading font-semibold mb-3">Top Jobs by Material Cost</h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
              <BarChart data={topJobs} layout="vertical" margin={{ left: 30 }}>
                <CartesianGrid stroke="#1E293B" horizontal={false} />
                <XAxis type="number" stroke="#94A3B8" tick={{ fontSize: 10, fill: "#94A3B8" }} />
                <YAxis dataKey="job" type="category" stroke="#94A3B8" tick={{ fontSize: 10, fill: "#94A3B8" }} width={120} />
                <Tooltip contentStyle={{ background: "#0A0B10", border: "1px solid #1E293B", borderRadius: 0 }}
                         formatter={(v) => `$${Number(v).toLocaleString()}`} />
                <Bar dataKey="cost" fill="#10B981" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>

      {/* MATERIAL COST BY WEEK */}
      <section className="bg-[#12141D] border border-border p-5">
        <h3 className="font-heading font-semibold mb-3">Material Cost by Week</h3>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
            <BarChart data={matWeek}>
              <CartesianGrid stroke="#1E293B" vertical={false} />
              <XAxis dataKey="week" stroke="#94A3B8" tick={{ fontSize: 10, fill: "#94A3B8" }}
                     tickFormatter={(v) => v?.slice(5)} />
              <YAxis stroke="#94A3B8" tick={{ fontSize: 10, fill: "#94A3B8" }}
                     tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
              <Tooltip contentStyle={{ background: "#0A0B10", border: "1px solid #1E293B", borderRadius: 0 }}
                       formatter={(v) => `$${Number(v).toLocaleString()}`} />
              <Bar dataKey="cost" fill="#10B981" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </section>

      {/* HOURS HEATMAP */}
      {heatmap && <HoursHeatmap data={heatmap} />}

      {/* MATERIAL COST TREEMAP */}
      <MaterialTreemap data={matAll} showMexico={showMexico} />

      {/* KPI DRILL-DOWN MODAL */}
      <KpiDrillModal
        kind={drill}
        open={!!drill}
        onClose={() => setDrill(null)}
        filter={{ ...filter, show_mexico: showMexico }}
      />

      {/* PRODUCTION BY DEPARTMENT DRILL-DOWN */}
      <KpiDrillModal
        kind={deptDrill ? "production" : null}
        open={!!deptDrill}
        onClose={() => setDeptDrill(null)}
        filter={{ ...filter, show_mexico: showMexico }}
        department={deptDrill?.department}
        titleOverride={deptDrill ? `${deptDrill.department} · Jobs Breakdown${showMexico ? " · Mexico" : ""}` : null}
      />
    </div>
  );
}
